import CustomPagination from "components/CustomPagination";
import Loading from "components/Loading";
import { useTooltip } from "context/TooltipProvider";
import React, { useEffect, useState } from "react";
import {
  DoubtfulCaseStatus,
  InvestigationFile,
  PrivateProsecution,
  PrivateProsecutionForwardingStatus,
  PrivateProsecutionStatus,
} from "types";

type Props = {
  file: InvestigationFile;
  crimeReferenceNumber?: string;
  incidentRegistrationNumber?: string;
  setSelectedId: React.Dispatch<React.SetStateAction<string | number>>;
  setPageType: React.Dispatch<React.SetStateAction<"list" | "detail">>;
};

const ITEMS_PER_PAGE = 9;

const PrivateProsecutionList: React.FC<Props> = ({
  file,
  crimeReferenceNumber,
  incidentRegistrationNumber,
  setSelectedId,
  setPageType,
}) => {
  const { showTooltip } = useTooltip();

  const [instructions, setInstructions] = useState<PrivateProsecution[]>([]);

  useEffect(() => {
    if (file?.private_prosecutions) {
      setInstructions(file.private_prosecutions);
    }
  }, [file]);

  // const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  // const navigate = useNavigate();

  // const searchTerm = search.trim().toLowerCase();
  // const filtered = instructions.filter((item) => {
  //   const incident_registration_number =
  //     item?.incident_registration_number?.toString()?.toLowerCase() || "";
  //   const crime_registration_number =
  //     item?.crime_registration_number?.toString()?.toLowerCase() || "";
  //   const prosecutor_registration_number =
  //     item?.prosecutor_registration_number?.toString()?.toLowerCase() || "";
  //   const status = item?.request_status?.toString()?.toLowerCase() || "";
  //   const matchesText =
  //     incident_registration_number.includes(searchTerm) ||
  //     crime_registration_number.includes(searchTerm) ||
  //     prosecutor_registration_number.includes(searchTerm) ||
  //     status.includes(searchTerm);
  //   return matchesText;
  // });

  // Pagination logic
  const pageCount = Math.ceil(instructions.length / ITEMS_PER_PAGE);
  const paginated = instructions.slice(
    currentPage * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE + ITEMS_PER_PAGE,
  );

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div className="max-w-screen-xl space-y-6">
      <div className="max-w-screen-xl ml-1 mr-4 space-y-6">
        <div className="overflow-hidden bg-white shadow rounded-xl">
          {/* <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 p-4 border-b">
            <div className="relative w-full md:w-80">
              <span className="absolute inset-y-0 flex items-center text-gray-500 pointer-events-none left-3">
                🔍
              </span>
              <input
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(0);
                }}
                placeholder="Search"
                className="w-full py-2 pl-10 pr-4 text-sm text-black placeholder-gray-600 bg-gray-200 rounded-md focus:outline-none"
              />
            </div>
          </div> */}
          {paginated.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              No instructions found.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-4">
              {paginated.map((item, idx) => (
                <div
                  key={item.id || idx}
                  className="bg-white border border-gray-200 rounded-xl shadow-sm p-6 flex flex-col gap-2 transition-all duration-150 hover:shadow-lg"
                >
                  <div className="flex flex-row justify-between items-center mb-2">
                    <span className="font-semibold text-[#AA232F] text-base">
                      {item.applicant_name}
                    </span>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-semibold shadow-sm
                        ${
                          item.status === "pending"
                            ? "bg-yellow-50 text-yellow-800 border border-yellow-200"
                            : item.status === "approved"
                              ? "bg-green-50 text-green-800 border border-green-200"
                              : item.status === "rejected"
                                ? "bg-red-50 text-red-800 border border-red-200"
                                : item.status === "withdrawn"
                                  ? "bg-blue-50 text-blue-800 border border-blue-200"
                                  : item.status === "on_litigation"
                                    ? "bg-indigo-50 text-indigo-800 border border-indigo-200"
                                    : item.status === "final_verdict_given"
                                      ? "bg-purple-50 text-purple-800 border border-purple-200"
                                      : item.status === "on_appeal"
                                        ? "bg-pink-50 text-pink-800 border border-pink-200"
                                        : "bg-gray-50 text-gray-700 border border-gray-200"
                        }
                      `}
                    >
                      {item.status && PrivateProsecutionStatus[item.status]}
                    </span>
                  </div>
                  <div className="flex flex-col gap-1 mb-2">
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">Phone No.:</span>{" "}
                      {item.applicant_phone_number}
                    </span>
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">City:</span>{" "}
                      {item.applicant_city}
                    </span>
                    <span className="text-sm text-gray-700">
                      <span className="font-semibold">Forward Status:</span>{" "}
                      {item?.forwarding_status &&
                        PrivateProsecutionForwardingStatus[
                          item.forwarding_status
                        ]}
                    </span>
                  </div>
                  <div className="flex flex-row justify-end gap-2 mt-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedId(item?.id || "");
                        setPageType("detail");
                      }}
                      className="px-4 py-1 text-sm bg-[#212121] text-white rounded hover:bg-[#AA232F] transition"
                    >
                      View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
          {/* Pagination */}
          {pageCount > 1 && (
            <div className="flex justify-center my-8">
              <CustomPagination
                pageCount={pageCount}
                currentPage={currentPage}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PrivateProsecutionList;
